import type { ModeResult, TypeCode } from '@mindtype/shared'

interface ColorConfig {
  primary: string
  secondary: string
  text: string
}

interface ModeOverviewProps {
  modes: ModeResult
  onTypeClick?: (typeCode: string) => void
  colorConfig?: ColorConfig
}

const MODE_LABELS = {
  natural: { name: '日常模式', desc: '放松、自主的状态' },
  work: { name: '工作模式', desc: '有任务、有要求的环境' },
  stress: { name: '压力模式', desc: '紧迫、高压的情境' },
}

const TYPE_NAMES: Record<TypeCode, string> = {
  'ASID': '逻辑自治型',
  'ASIO': '架构探险型',
  'ASED': '战略统筹型',
  'ASEO': '博弈开拓型',
  'ACID': '匠人沉潜型',
  'ACIO': '灵性体验型',
  'ACED': '策展守护型',
  'ACEO': '体验创造型',
  'RSID': '准则效忠型',
  'RSIO': '解构评论型',
  'RSED': '功利架构型',
  'RSEO': '冒险竞争型',
  'RCID': '共情调和型',
  'RCIO': '情感雷达型',
  'RCED': '务实传统型',
  'RCEO': '表演情境型',
}

// 默认配色
const DEFAULT_COLOR = { primary: '#3b82f6', secondary: '#60a5fa', text: '#ffffff' }

export function ModeOverview({ modes, onTypeClick, colorConfig = DEFAULT_COLOR }: ModeOverviewProps) {
  const isAllSame = modes.natural === modes.work && modes.work === modes.stress
  const color = colorConfig

  return (
    <div
      className="bg-white rounded-xl p-6"
      style={{ border: `1px solid ${color.primary}15` }}
    >
      <h2 className="text-base font-semibold text-gray-900 mb-4">三层模式总览</h2>

      {isAllSame && (
        <div
          className="rounded-lg p-3 mb-4"
          style={{ backgroundColor: `${color.primary}10`, border: `1px solid ${color.primary}20` }}
        >
          <p className="text-sm" style={{ color: color.primary }}>
            <span className="font-medium">高度一致</span>
            <span className="mx-1">·</span>
            你在不同情境下保持相同的认知风格
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {(['natural', 'work', 'stress'] as const).map((mode, index) => {
          const typeCode = modes[mode]
          const label = MODE_LABELS[mode]
          const typeName = TYPE_NAMES[typeCode] || typeCode

          return (
            <button
              key={mode}
              onClick={() => onTypeClick?.(typeCode)}
              className="rounded-lg p-4 text-center transition-all hover:shadow-md cursor-pointer"
              style={{ backgroundColor: `${color.primary}08` }}
            >
              <div
                className="w-10 h-10 rounded-full mx-auto mb-3 flex items-center justify-center"
                style={{ backgroundColor: `${color.primary}15` }}
              >
                <span className="text-sm font-medium" style={{ color: color.primary }}>{index + 1}</span>
              </div>
              <div className="text-xs mb-2" style={{ color: color.primary }}>{label.name}</div>
              <div className="text-xl font-semibold text-gray-900 mb-1 tracking-wider font-mono">
                {typeCode}
              </div>
              <div className="text-sm text-gray-700 mb-1">{typeName}</div>
              <div className="text-xs text-gray-500">{label.desc}</div>
              <div className="text-xs mt-2" style={{ color: color.primary }}>点击查看详情</div>
            </button>
          )
        })}
      </div>

      {!isAllSame && (
        <div className="mt-4 text-center">
          <p className="text-xs" style={{ color: color.primary }}>
            你的认知风格在不同情境下有所变化，这是正常的适应性表现
          </p>
        </div>
      )}
    </div>
  )
}
